import type {
  DateInput,
  KstDateString,
  KstParts,
  UtcIsoString,
} from '../types/datetime.type';

/** 한국은 서머타임이 없어 오프셋을 고정해도 안전하다. */
export const SEOUL_TZ = 'Asia/Seoul';
export const KST_OFFSET = '+09:00';

const MS_PER_DAY = 86_400_000;
const PAD_WIDTH = 2;
const FIRST_DAY = 1;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;
const DATETIME_INPUT_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/;
const HAS_OFFSET_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** 파트 추출 전용 — `hourCycle: 'h23'` 이어야 자정이 `24:00` 으로 찍히지 않는다. */
const KST_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: SEOUL_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const KST_WEEKDAY = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  weekday: 'short',
});

const pad2 = (n: number) => String(n).padStart(PAD_WIDTH, '0');

/**
 * 응답 문자열 → 시각. 날짜만 있으면 KST 자정, 오프셋 없는 시각은 서버 계약대로 UTC 로 읽는다.
 * `new Date(str)` 를 직접 쓰면 오프셋 없는 값이 브라우저 타임존으로 해석된다.
 */
function toInstant(value: DateInput): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = value.trim();
  if (!raw) return null;

  let normalized = raw;
  if (DATE_ONLY_RE.test(raw)) normalized = `${raw}T00:00:00${KST_OFFSET}`;
  else if (!HAS_OFFSET_RE.test(raw)) normalized = `${raw.replace(' ', 'T')}Z`;

  const instant = new Date(normalized);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/** 브라우저 타임존과 무관한 KST 달력·시각 파트. 잘못된 값이면 null. */
export function getKstParts(value: DateInput): KstParts | null {
  const instant = toInstant(value);
  if (!instant) return null;

  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of KST_FORMAT.formatToParts(instant)) parts[part.type] = part.value;
  const { year = '', month = '', day = '', hour = '', minute = '' } = parts;

  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    date: `${year}-${month}-${day}` as KstDateString,
    time: `${hour}:${minute}`,
  };
}

export function toUtcIso(value: Date | number): UtcIsoString {
  return new Date(value).toISOString() as UtcIsoString;
}

export function isKstDateString(value: string): value is KstDateString {
  return DATE_ONLY_RE.test(value) && toInstant(value) !== null;
}

/* ── 표시 ─────────────────────────────────────────────────────── */

/** `2026-10-15` (KST). */
export function formatKstDate(value?: DateInput | null, fallback = '-'): string {
  if (value == null) return fallback;
  return getKstParts(value)?.date ?? fallback;
}

/** `2026-10-15 09:00` (KST). */
export function formatKstDateTime(value?: DateInput | null, fallback = '-'): string {
  if (value == null) return fallback;
  const parts = getKstParts(value);
  return parts ? `${parts.date} ${parts.time}` : fallback;
}

/** `2026-06-30 (수)` — 서버 알림톡 날짜 표기(admin-service `formatEducationDate`)와 같은 형태. */
export function formatKstDateWithWeekday(value?: DateInput | null, fallback = '-'): string {
  if (value == null) return fallback;
  const instant = toInstant(value);
  const parts = instant && getKstParts(instant);
  return instant && parts ? `${parts.date} (${KST_WEEKDAY.format(instant)})` : fallback;
}

/** `2026-10-15 10:00 ~ 18:00`. 같은 날이면 뒤쪽 날짜를 생략한다. */
export function formatKstDateTimeRange(
  start?: DateInput | null,
  end?: DateInput | null,
  fallback = '-',
): string {
  const from = start == null ? null : getKstParts(start);
  if (!from) return fallback;
  const to = end == null ? null : getKstParts(end);
  if (!to) return `${from.date} ${from.time}`;
  return from.date === to.date
    ? `${from.date} ${from.time} ~ ${to.time}`
    : `${from.date} ${from.time} ~ ${to.date} ${to.time}`;
}

/** `2026-10` (KST). */
export function formatKstMonthKey(value: DateInput = new Date()): string {
  const parts = getKstParts(value);
  return parts ? `${parts.year}-${pad2(parts.month)}` : '';
}

/* ── 폼 입력 ↔ 서버 ────────────────────────────────────────────── */

/** `<input type="date">` 값. 비거나 잘못된 값이면 ''. */
export function toKstDateInput(value?: DateInput | null): string {
  return value == null ? '' : (getKstParts(value)?.date ?? '');
}

/** `<input type="time">` 값. */
export function toKstTimeInput(value?: DateInput | null): string {
  return value == null ? '' : (getKstParts(value)?.time ?? '');
}

/** `<input type="datetime-local">` 값 `YYYY-MM-DDTHH:mm` (KST). */
export function toKstDateTimeInput(value?: DateInput | null): string {
  const parts = value == null ? null : getKstParts(value);
  return parts ? `${parts.date}T${parts.time}` : '';
}

/** KST `YYYY-MM-DD` + `HH:mm` → UTC ISO. 형식이 어긋나면 null. */
export function fromKstParts(date: string, time: string): UtcIsoString | null {
  if (!DATE_ONLY_RE.test(date) || !TIME_RE.test(time)) return null;
  const instant = new Date(`${date}T${time}:00${KST_OFFSET}`);
  return Number.isNaN(instant.getTime()) ? null : toUtcIso(instant);
}

/** `datetime-local` 값(KST) → UTC ISO. */
export function fromKstDateTimeInput(value: string): UtcIsoString | null {
  const match = DATETIME_INPUT_RE.exec(value);
  return match ? fromKstParts(match[1], match[2]) : null;
}

/** KST 날짜의 시작 시각(00:00:00.000) → UTC ISO. */
export function kstDayStart(date: string): UtcIsoString | null {
  return fromKstParts(date, '00:00');
}

/** KST 날짜의 마지막 시각(23:59:59.999) → UTC ISO. */
export function kstDayEnd(date: string): UtcIsoString | null {
  const start = kstDayStart(date);
  return start && toUtcIso(Date.parse(start) + MS_PER_DAY - 1);
}

/* ── 오늘·달력 계산 ───────────────────────────────────────────── */

export function kstNow(): { date: KstDateString; time: string } {
  const { date, time } = getKstParts(new Date()) as KstParts;
  return { date, time };
}

export function kstToday(): KstDateString {
  return kstNow().date;
}

function splitDate(date: string): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number);
  return [y, m, d];
}

function utcCalendarToDate(utcMs: number): KstDateString {
  const d = new Date(utcMs);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}` as KstDateString;
}

/** KST 날짜 덧셈. 달력 산술만 하므로 자정 근처에서 하루가 밀리지 않는다. */
export function addKstDays(date: string, days: number): KstDateString {
  const [y, m, d] = splitDate(date);
  return utcCalendarToDate(Date.UTC(y, m - 1, d) + days * MS_PER_DAY);
}

/** 해당 날짜가 속한 달의 1일에서 `monthOffset` 만큼 이동한 달의 1일. */
export function startOfKstMonth(date: string, monthOffset = 0): KstDateString {
  const [y, m] = splitDate(date);
  return utcCalendarToDate(Date.UTC(y, m - 1 + monthOffset, FIRST_DAY));
}

/**
 * KST 달력 기준 일수 차이(`target - from`). 같은 KST 날짜면 0.
 * 밀리초 차이를 24h 로 나누면 자정 경계에서 D-day 가 하루 어긋난다.
 */
export function kstDayDiff(target: DateInput, from: DateInput = new Date()): number | null {
  const a = getKstParts(target);
  const b = getKstParts(from);
  if (!a || !b) return null;
  return (
    (Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day)) / MS_PER_DAY
  );
}

/** 시각 비교용 epoch ms. 잘못된 값이면 null. */
export function toEpochMs(value: DateInput): number | null {
  return toInstant(value)?.getTime() ?? null;
}
